import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { systemApi } from '../../utils/api';
import usePageMetaStore from '../../store/pageMetaStore';
import {
  Activity,
  Server,
  Mail,
  Inbox,
  DollarSign,
  Database,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  Wifi,
  WifiOff,
  Zap,
  Clock,
  HardDrive,
  Table2,
  FileText,
  Users,
  Package,
  ShoppingCart,
  Wrench,
  BookOpen,
  Bell,
  DownloadCloud,
  Webhook,
  Send,
  Sparkles
} from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';

// Circular Gauge Component
const CircularGauge = ({ value, max = 100, size = 120, strokeWidth = 8, color = 'blue', label, sublabel }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const percent = Math.min(Math.max(value / max, 0), 1);
  const offset = circumference - percent * circumference;

  const colors = {
    blue: { stroke: '#3B82F6', bg: '#E5E7EB', text: 'text-blue-600' },
    green: { stroke: '#10B981', bg: '#E5E7EB', text: 'text-green-600' },
    red: { stroke: '#EF4444', bg: '#E5E7EB', text: 'text-red-600' },
    amber: { stroke: '#F59E0B', bg: '#E5E7EB', text: 'text-amber-600' },
    purple: { stroke: '#8B5CF6', bg: '#E5E7EB', text: 'text-purple-600' },
  };

  const c = colors[color] || colors.blue;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg className="transform -rotate-90" width={size} height={size}>
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={c.bg}
            strokeWidth={strokeWidth}
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={c.stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            className="transition-all duration-700 ease-out"
          />
        </svg>
        {/* Center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={clsx('text-2xl font-bold', c.text)}>{value}</span>
          {max !== 100 && <span className="text-xs text-gray-400">/ {max}</span>}
        </div>
      </div>
      {label && <p className="mt-2 text-sm font-medium text-gray-700">{label}</p>}
      {sublabel && <p className="text-xs text-gray-500">{sublabel}</p>}
    </div>
  );
};

// Status Indicator with pulse animation
const StatusIndicator = ({ status, label, sublabel, latency }) => {
  const statusConfig = {
    ok: { color: 'bg-green-500', ring: 'ring-green-400', icon: Wifi, text: 'Conectado' },
    warning: { color: 'bg-amber-500', ring: 'ring-amber-400', icon: AlertTriangle, text: 'Advertencia' },
    error: { color: 'bg-red-500', ring: 'ring-red-400', icon: WifiOff, text: 'Error' },
    no_config: { color: 'bg-gray-400', ring: 'ring-gray-300', icon: AlertTriangle, text: 'Sin Config' },
  };

  const config = statusConfig[status] || statusConfig.no_config;
  const Icon = config.icon;

  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        <div className={clsx(
          'w-4 h-4 rounded-full',
          config.color,
          status === 'ok' && 'animate-pulse'
        )} />
        {status === 'ok' && (
          <div className={clsx(
            'absolute inset-0 w-4 h-4 rounded-full ring-4 animate-ping opacity-30',
            config.ring
          )} />
        )}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{label}</span>
          {latency && (
            <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
              {latency}ms
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500">{sublabel || config.text}</p>
      </div>
      <Icon className={clsx('h-5 w-5', status === 'ok' ? 'text-green-500' : status === 'error' ? 'text-red-500' : 'text-gray-400')} />
    </div>
  );
};

// Service Card with live status - Outline style
const ServiceCard = ({ title, icon: Icon, status, message, latency, ambiente, error, children }) => {
  const isOk = status === 'ok';
  const isError = status === 'error';
  const isWarning = status === 'warning' || status === 'no_config';

  return (
    <div className={clsx(
      'relative overflow-hidden rounded-2xl border-2 p-6 transition-all duration-300 bg-white',
      isOk && 'border-green-400',
      isError && 'border-red-400',
      isWarning && 'border-amber-400',
      !isOk && !isError && !isWarning && 'border-gray-300'
    )}>
      {/* Decorative cloud behind status badge */}
      <div className={clsx(
        'absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-30',
        isOk && 'bg-green-300',
        isError && 'bg-red-300',
        isWarning && 'bg-amber-300'
      )} />

      <div className="relative">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={clsx(
              'p-3 rounded-xl border-2',
              isOk && 'border-green-400 text-green-600',
              isError && 'border-red-400 text-red-600',
              isWarning && 'border-amber-400 text-amber-600',
              !isOk && !isError && !isWarning && 'border-gray-300 text-gray-600'
            )}>
              <Icon className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{title}</h3>
              {ambiente && (
                <span className="text-xs px-2 py-0.5 bg-white/50 rounded-full text-gray-600">
                  {ambiente === 'production' ? 'Produccion' : 'Desarrollo'}
                </span>
              )}
            </div>
          </div>

          {/* Status badge - cloud style with fill */}
          <div className={clsx(
            'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium',
            isOk && 'bg-green-100 text-green-700',
            isError && 'bg-red-100 text-red-700',
            isWarning && 'bg-amber-100 text-amber-700'
          )}>
            <div className={clsx(
              'w-2 h-2 rounded-full',
              isOk && 'bg-green-500 animate-pulse',
              isError && 'bg-red-500',
              isWarning && 'bg-amber-500'
            )} />
            {isOk ? 'Online' : isError ? 'Offline' : 'Alerta'}
          </div>
        </div>

        {/* Main content */}
        <div className="space-y-3">
          <p className={clsx(
            'text-lg font-medium',
            isOk && 'text-green-700',
            isError && 'text-red-700',
            isWarning && 'text-amber-700'
          )}>
            {message}
          </p>

          {latency && (
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-gray-400" />
              <span className="text-sm text-gray-600">Latencia: {latency}ms</span>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 bg-red-100 rounded-lg p-2">
              {error}
            </p>
          )}

          {children}
        </div>
      </div>
    </div>
  );
};

// ─── Helpers para tab "Procesos" (todas las automatizaciones del sistema) ───

const JOB_COLORS = {
  blue:    { border: 'border-blue-400',    bg: 'bg-blue-300',    iconText: 'text-blue-600',    pill: 'bg-blue-100' },
  purple:  { border: 'border-purple-400',  bg: 'bg-purple-300',  iconText: 'text-purple-600',  pill: 'bg-purple-100' },
  amber:   { border: 'border-amber-400',   bg: 'bg-amber-300',   iconText: 'text-amber-600',   pill: 'bg-amber-100' },
  emerald: { border: 'border-emerald-400', bg: 'bg-emerald-300', iconText: 'text-emerald-600', pill: 'bg-emerald-100' },
  cyan:    { border: 'border-cyan-400',    bg: 'bg-cyan-300',    iconText: 'text-cyan-600',    pill: 'bg-cyan-100' },
  indigo:  { border: 'border-indigo-400',  bg: 'bg-indigo-300',  iconText: 'text-indigo-600',  pill: 'bg-indigo-100' },
  rose:    { border: 'border-rose-400',    bg: 'bg-rose-300',    iconText: 'text-rose-600',    pill: 'bg-rose-100' },
  slate:   { border: 'border-slate-400',   bg: 'bg-slate-300',   iconText: 'text-slate-600',   pill: 'bg-slate-100' }
};

const statusDot = (status) => {
  if (status === 'error') return 'bg-red-500';
  if (status === 'paused' || status === 'idle' || status === 'disabled') return 'bg-amber-500';
  return 'bg-green-500';
};
const statusText = (status) => {
  if (status === 'error') return 'text-red-600';
  if (status === 'paused' || status === 'idle' || status === 'disabled') return 'text-amber-600';
  return 'text-green-600';
};

function JobCard({ color = 'blue', icon: Icon, title, description, status, statusLabel, children }) {
  const c = JOB_COLORS[color] || JOB_COLORS.blue;
  return (
    <div className={clsx('relative overflow-hidden rounded-2xl border-2 p-6 bg-white min-h-[200px]', c.border)}>
      <div className={clsx('absolute -top-10 -right-10 w-32 h-32 rounded-full opacity-30', c.bg)} />
      <div className="relative">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className={clsx('p-3 rounded-xl border-2', c.border, c.iconText)}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 truncate">{title}</h3>
              <p className="text-sm text-gray-500 truncate">{description}</p>
            </div>
          </div>
          <div className={clsx('flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium shrink-0', c.pill, statusText(status))}>
            <div className={clsx('w-2 h-2 rounded-full animate-pulse', statusDot(status))} />
            {statusLabel}
          </div>
        </div>
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}

function Metric({ label, value, sublabel, alert = false }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-700">{label}</p>
        {sublabel && <p className={clsx('text-xs truncate', alert ? 'text-amber-600' : 'text-gray-500')}>{sublabel}</p>}
      </div>
      <span className={clsx('font-bold text-lg shrink-0', alert ? 'text-amber-600' : 'text-gray-900')}>{value}</span>
    </div>
  );
}

// Fecha relativa simple (sin libs): "hace 5min", "hace 2h", "hace 3d"
function fmtRel(dateLike) {
  if (!dateLike) return '-';
  const d = new Date(dateLike);
  if (isNaN(d.getTime())) return '-';
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return 'hace ' + sec + 's';
  if (sec < 3600) return 'hace ' + Math.floor(sec / 60) + 'min';
  if (sec < 86400) return 'hace ' + Math.floor(sec / 3600) + 'h';
  return 'hace ' + Math.floor(sec / 86400) + 'd';
}
function fmtDate(dateLike) {
  if (!dateLike) return '-';
  const d = new Date(dateLike);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function EstadoSistema() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'dashboard');

  // Update URL when tab changes
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };
  const [health, setHealth] = useState(null);
  const [emailLogs, setEmailLogs] = useState(null);
  const [jobs, setJobs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [optimizing, setOptimizing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const setPageLastUpdated = usePageMetaStore(s => s.setLastUpdated);

  // Email logs filters
  const [emailPage, setEmailPage] = useState(1);
  const [emailStatus, setEmailStatus] = useState('all');
  const [emailSearch, setEmailSearch] = useState('');

  const loadHealth = useCallback(async () => {
    try {
      const res = await systemApi.getHealth();
      setHealth(res.data.data);
      const now = new Date();
      setLastUpdate(now);
      setPageLastUpdated(now);  // header global
      return true;
    } catch (error) {
      console.error('Error loading health:', error);
      setError(error.response?.data?.error || error.message);
      return false;
    }
  }, []);

  const loadEmailLogs = useCallback(async () => {
    try {
      const res = await systemApi.getEmailLogs({
        page: emailPage,
        limit: 25,
        status: emailStatus,
        search: emailSearch
      });
      setEmailLogs(res.data);
      return true;
    } catch (error) {
      console.error('Error loading email logs:', error);
      return false;
    }
  }, [emailPage, emailStatus, emailSearch]);

  const loadJobs = useCallback(async () => {
    try {
      const res = await systemApi.getJobs();
      setJobs(res.data.data);
      return true;
    } catch (error) {
      console.error('Error loading jobs:', error);
      return false;
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([loadHealth(), loadEmailLogs(), loadJobs()]);
    } catch (e) {
      console.error('Error loading all:', e);
    } finally {
      setLoading(false);
    }
  }, [loadHealth, loadEmailLogs, loadJobs]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (activeTab === 'emails') {
      loadEmailLogs();
    }
  }, [emailPage, emailStatus, emailSearch, activeTab]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (activeTab === 'dashboard') {
        loadHealth();
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [activeTab, loadHealth]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAll();
    setRefreshing(false);
    toast.success('Estado actualizado');
  };

  const handleOptimizeTables = async () => {
    setOptimizing(true);
    try {
      const response = await systemApi.optimizeTables();
      if (response.data?.success) {
        toast.success(response.data.data?.message || 'Tablas optimizadas');
        // Refresh health to see updated fragmentation status
        await loadHealth();
      } else {
        toast.error(response.data?.data?.message || 'Error al optimizar');
      }
    } catch (err) {
      console.error('Error optimizing tables:', err);
      toast.error('Error al optimizar tablas');
    } finally {
      setOptimizing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const tabs = [
    { id: 'dashboard', label: 'Monitoreo', icon: Activity },
    { id: 'emails', label: 'Email Logs', icon: Mail },
    { id: 'jobs', label: 'Procesos', icon: Server }
  ];

  // Extract latency from message like "Conectado (123ms)"
  const extractLatency = (message) => {
    const match = message?.match(/\((\d+)ms\)/);
    return match ? parseInt(match[1]) : null;
  };

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex items-center justify-between">
        <p className="text-gray-500">
          Monitoreo en tiempo real
          {/* "Actualizado: HH:MM:SS" ahora se muestra en el header global (pageMetaStore) */}
        </p>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 border-2 border-blue-500 text-blue-600 rounded-xl hover:bg-blue-50 transition-all disabled:opacity-50"
        >
          <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {/* Tabs - Outline style */}
      <div className="inline-flex gap-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all border-2',
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600 bg-blue-50'
                : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
            )}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 rounded-full border-4 border-blue-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-blue-500 border-t-transparent animate-spin"></div>
          </div>
          <p className="text-gray-500">Verificando servicios...</p>
        </div>
      ) : error && !health ? (
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-8 text-center">
          <XCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-red-800 mb-2">Error de conexion</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleRefresh}
            className="px-6 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-colors"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <>
          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && health && (
            <div className="space-y-6">
              {/* Service Status Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <ServiceCard
                  title="Hacienda API"
                  icon={Server}
                  status={health.services.hacienda?.status}
                  message={health.services.hacienda?.message}
                  latency={extractLatency(health.services.hacienda?.message)}
                  ambiente={health.services.hacienda?.ambiente}
                  error={health.services.hacienda?.error}
                >
                  {health.services.hacienda?.stats24h && (
                    <div className="mt-4 pt-4 border-t border-gray-200/50">
                      <p className="text-xs text-gray-500 mb-2">Ultimas 24h</p>
                      <div className="flex items-center gap-4">
                        <CircularGauge
                          value={health.services.hacienda.stats24h.successRate}
                          size={70}
                          strokeWidth={6}
                          color={health.services.hacienda.stats24h.successRate >= 90 ? 'green' : health.services.hacienda.stats24h.successRate >= 70 ? 'amber' : 'red'}
                          label="Exito"
                        />
                        <div className="flex-1 grid grid-cols-3 gap-2 text-center">
                          <div>
                            <p className="text-lg font-bold text-green-600">{health.services.hacienda.stats24h.aceptados}</p>
                            <p className="text-xs text-gray-500">Aceptados</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-red-600">{health.services.hacienda.stats24h.rechazados}</p>
                            <p className="text-xs text-gray-500">Rechazados</p>
                          </div>
                          <div>
                            <p className="text-lg font-bold text-amber-600">{health.services.hacienda.stats24h.pendientes}</p>
                            <p className="text-xs text-gray-500">Pendientes</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </ServiceCard>

                <ServiceCard
                  title="Email Envio (FE)"
                  icon={Mail}
                  status={health.services.email?.status}
                  message={health.services.email?.message}
                  latency={extractLatency(health.services.email?.message)}
                  error={health.services.email?.error}
                >
                  {health.services.email?.fromEmail && (
                    <p className="text-sm text-gray-600">
                      Cuenta: {health.services.email.fromEmail}
                    </p>
                  )}
                  {health.services.email?.provider && (
                    <p className="text-xs text-gray-500 mt-0.5">
                      {health.services.email.provider === 'smtp'
                        ? `SMTP · ${health.services.email.smtpHost || ''}`
                        : 'Office 365 (Microsoft Graph)'}
                    </p>
                  )}
                  {health.services.email?.statsToday && health.services.email.statsToday.total > 0 && (
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <span className="text-green-600">{health.services.email.statsToday.enviados} enviados</span>
                      {health.services.email.statsToday.fallidos > 0 && (
                        <span className="text-red-600">{health.services.email.statsToday.fallidos} fallidos</span>
                      )}
                    </div>
                  )}
                </ServiceCard>

                <ServiceCard
                  title="Email Gastos (Recepcion)"
                  icon={Inbox}
                  status={health.services.gastosEmail?.status}
                  message={health.services.gastosEmail?.message}
                  latency={extractLatency(health.services.gastosEmail?.message)}
                  error={health.services.gastosEmail?.error}
                >
                  {health.services.gastosEmail?.mailbox && (
                    <p className="text-sm text-gray-600">
                      Bandeja: {health.services.gastosEmail.mailbox}
                    </p>
                  )}
                  {health.services.gastosEmail?.lastCheck && (
                    <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                      <Clock className="h-4 w-4" />
                      <span>{health.services.gastosEmail.lastCheck.display}</span>
                    </div>
                  )}
                  {health.services.gastosEmail?.statsToday && health.services.gastosEmail.statsToday.total > 0 && (
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <span className="text-green-600">{health.services.gastosEmail.statsToday.importadas} importadas</span>
                      {health.services.gastosEmail.statsToday.duplicadas > 0 && (
                        <span className="text-amber-600">{health.services.gastosEmail.statsToday.duplicadas} duplicadas</span>
                      )}
                      {health.services.gastosEmail.statsToday.errores > 0 && (
                        <span className="text-red-600">{health.services.gastosEmail.statsToday.errores} errores</span>
                      )}
                    </div>
                  )}
                </ServiceCard>

                <ServiceCard
                  title="Tipo de Cambio (BCCR)"
                  icon={DollarSign}
                  status={health.services.exchangeRate?.status}
                  message={health.services.exchangeRate?.message}
                  error={health.services.exchangeRate?.error}
                >
                  {health.services.exchangeRate?.rate && (
                    <div className="mt-2 flex items-center gap-6">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-blue-600">
                          {health.services.exchangeRate.rate.venta.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">Venta</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-gray-600">
                          {health.services.exchangeRate.rate.compra.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500">Compra</p>
                      </div>
                    </div>
                  )}
                </ServiceCard>

                <ServiceCard
                  title="Base de Datos"
                  icon={Database}
                  status={health.services.database?.status}
                  message={health.services.database?.message}
                  latency={health.services.database?.latency}
                >
                  {health.services.database && (
                    <div className="mt-3 space-y-3">
                      {/* Size Info */}
                      {health.services.database.size && (
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="bg-blue-50 rounded-lg p-2">
                            <HardDrive className="h-4 w-4 text-blue-600 mx-auto mb-1" />
                            <p className="text-lg font-bold text-blue-700">{health.services.database.size.totalSize}</p>
                            <p className="text-xs text-gray-500">MB Total</p>
                          </div>
                          <div className="bg-purple-50 rounded-lg p-2">
                            <Table2 className="h-4 w-4 text-purple-600 mx-auto mb-1" />
                            <p className="text-lg font-bold text-purple-700">{health.services.database.size.tableCount}</p>
                            <p className="text-xs text-gray-500">Tablas</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2">
                            <Zap className="h-4 w-4 text-gray-600 mx-auto mb-1" />
                            <p className={clsx(
                              'text-lg font-bold',
                              health.services.database.latency < 50 ? 'text-green-600' :
                              health.services.database.latency < 100 ? 'text-amber-600' : 'text-red-600'
                            )}>{health.services.database.latency}ms</p>
                            <p className="text-xs text-gray-500">Latencia</p>
                          </div>
                        </div>
                      )}

                      {/* Record Counts */}
                      {health.services.database.recordCounts && (
                        <div className="border-t pt-2">
                          <p className="text-xs font-medium text-gray-500 mb-2">Registros Principales</p>
                          <div className="grid grid-cols-2 gap-1 text-xs">
                            {health.services.database.recordCounts.documentos !== undefined && (
                              <div className="flex items-center gap-1">
                                <FileText className="h-3 w-3 text-blue-500" />
                                <span className="text-gray-600">Documentos:</span>
                                <span className="font-medium">{health.services.database.recordCounts.documentos?.toLocaleString()}</span>
                              </div>
                            )}
                            {health.services.database.recordCounts.receptores !== undefined && (
                              <div className="flex items-center gap-1">
                                <Users className="h-3 w-3 text-green-500" />
                                <span className="text-gray-600">Clientes:</span>
                                <span className="font-medium">{health.services.database.recordCounts.receptores?.toLocaleString()}</span>
                              </div>
                            )}
                            {health.services.database.recordCounts.productos !== undefined && (
                              <div className="flex items-center gap-1">
                                <Package className="h-3 w-3 text-purple-500" />
                                <span className="text-gray-600">Productos:</span>
                                <span className="font-medium">{health.services.database.recordCounts.productos?.toLocaleString()}</span>
                              </div>
                            )}
                            {health.services.database.recordCounts.facturas_compra !== undefined && (
                              <div className="flex items-center gap-1">
                                <ShoppingCart className="h-3 w-3 text-orange-500" />
                                <span className="text-gray-600">Gastos:</span>
                                <span className="font-medium">{health.services.database.recordCounts.facturas_compra?.toLocaleString()}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Maintenance Warning */}
                      {health.services.database.needsMaintenance && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-amber-700">
                              <Wrench className="h-4 w-4" />
                              <span className="text-xs font-medium">
                                {health.services.database.fragmentedTables?.length} tabla(s) con fragmentación
                              </span>
                            </div>
                            <button
                              onClick={handleOptimizeTables}
                              disabled={optimizing}
                              className="px-2 py-1 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"
                            >
                              {optimizing ? (
                                <>
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                  Optimizando...
                                </>
                              ) : (
                                'Optimizar'
                              )}
                            </button>
                          </div>
                          <div className="mt-2 space-y-1">
                            {health.services.database.fragmentedTables?.map((table, idx) => (
                              <div key={idx} className="flex items-center justify-between text-xs bg-amber-100/50 rounded px-2 py-1">
                                <span className="font-mono text-amber-800">{table.name}</span>
                                <span className="text-amber-600">{table.fragPercent}% fragmentado</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Top Tables */}
                      {health.services.database.topTables && health.services.database.topTables.length > 0 && (
                        <details className="border-t pt-2">
                          <summary className="text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700">
                            Ver tablas más grandes
                          </summary>
                          <div className="mt-2 space-y-1 max-h-32 overflow-y-auto">
                            {health.services.database.topTables.slice(0, 5).map((table, idx) => (
                              <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                                <span className="text-gray-700 truncate">{table.name}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-gray-500">{table.rows?.toLocaleString()} rows</span>
                                  <span className="font-medium text-gray-700">{table.sizeMb} MB</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </ServiceCard>
              </div>
            </div>
          )}

          {/* Dashboard Tab - No data */}
          {activeTab === 'dashboard' && !health && (
            <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl p-12 text-center">
              <Activity className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Sin datos disponibles</h3>
              <p className="text-gray-500 mb-6">No se pudo obtener el estado del sistema.</p>
              <button
                onClick={handleRefresh}
                className="px-6 py-3 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors"
              >
                Cargar datos
              </button>
            </div>
          )}

          {/* Email Logs Tab */}
          {activeTab === 'emails' && (
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex items-center gap-4 bg-white rounded-xl border p-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por email, consecutivo..."
                    value={emailSearch}
                    onChange={(e) => { setEmailSearch(e.target.value); setEmailPage(1); }}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={emailStatus}
                  onChange={(e) => { setEmailStatus(e.target.value); setEmailPage(1); }}
                  className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">Todos</option>
                  <option value="sent">Enviados</option>
                  <option value="failed">Fallidos</option>
                  <option value="no_email">Sin Email</option>
                </select>
              </div>

              {/* Table */}
              <div className="bg-white rounded-xl border overflow-hidden">
                {emailLogs?.data?.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Mail className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                    No hay registros de email
                  </div>
                ) : (
                  <>
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Fecha</th>
                          <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600">Tipo</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Documento</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Cliente</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Email</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Estado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {emailLogs?.data?.map((log) => {
                          // Tipos: pueden ser de Hacienda (01-09, FE/NC/TE/FEC/FEE) o internos
                          // (estado_cuenta, api_alerta, pago_proveedor, reporte_recurrente, etc.)
                          const tipoLabels = {
                            '01': { label: 'FE', cls: 'bg-blue-100 text-blue-700' },
                            'FE': { label: 'FE', cls: 'bg-blue-100 text-blue-700' },
                            '03': { label: 'NC', cls: 'bg-purple-100 text-purple-700' },
                            'NC': { label: 'NC', cls: 'bg-purple-100 text-purple-700' },
                            '04': { label: 'TE', cls: 'bg-green-100 text-green-700' },
                            'TE': { label: 'TE', cls: 'bg-green-100 text-green-700' },
                            '08': { label: 'FEC', cls: 'bg-amber-100 text-amber-700' },
                            'FEC': { label: 'FEC', cls: 'bg-amber-100 text-amber-700' },
                            '09': { label: 'FEE', cls: 'bg-teal-100 text-teal-700' },
                            'FEE': { label: 'FEE', cls: 'bg-teal-100 text-teal-700' },
                            'cotizacion': { label: 'Cotiz', cls: 'bg-orange-100 text-orange-700' },
                            'estado_cuenta': { label: 'EstCta', cls: 'bg-violet-100 text-violet-700' },
                            'api_alerta': { label: 'API', cls: 'bg-pink-100 text-pink-700' },
                            'api_recordatorio': { label: 'API+', cls: 'bg-pink-100 text-pink-700' },
                            'pago_proveedor': { label: 'PagoP', cls: 'bg-emerald-100 text-emerald-700' },
                            'pago_aplicado': { label: 'PagoC', cls: 'bg-emerald-100 text-emerald-700' },
                            'reporte_recurrente': { label: 'Rep', cls: 'bg-cyan-100 text-cyan-700' },
                            'gastos_rechazo': { label: 'Recha', cls: 'bg-red-100 text-red-700' },
                            'test': { label: 'Test', cls: 'bg-gray-100 text-gray-600' }
                          };
                          const t = tipoLabels[log.documento_tipo] || { label: log.documento_tipo || '-', cls: 'bg-gray-100 text-gray-700' };

                          return (
                            <tr key={log.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {formatDate(log.sent_at)}
                              </td>
                              <td className="px-3 py-3 text-center">
                                <span className={clsx(
                                  'inline-flex items-center justify-center min-w-[44px] px-1.5 py-0.5 rounded text-xs font-semibold',
                                  t.cls
                                )}>
                                  {t.label}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <p className="font-mono text-sm text-gray-900">{log.documento_consecutivo || '-'}</p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-gray-900 truncate max-w-[200px]" title={log.receptor_nombre || '-'}>
                                  {log.receptor_nombre || '-'}
                                </p>
                              </td>
                              <td className="px-4 py-3">
                                <p className="text-sm text-gray-600">{log.recipient_email}</p>
                                {(log.cc_emails || log.bcc_emails) && (
                                  <div className="mt-1 flex flex-wrap gap-1">
                                    {log.cc_emails && (
                                      <span
                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700 border border-blue-200"
                                        title={`CC: ${log.cc_emails}`}
                                      >
                                        CC: {log.cc_emails.split(',').length}
                                      </span>
                                    )}
                                    {log.bcc_emails && (
                                      <span
                                        className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                        title={`BCC: ${log.bcc_emails}`}
                                      >
                                        BCC: {log.bcc_emails.split(',').length}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center">
                                <span className={clsx(
                                  'inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium',
                                  log.status === 'sent' && 'bg-green-100 text-green-700',
                                  log.status === 'failed' && 'bg-red-100 text-red-700',
                                  log.status === 'no_email' && 'bg-amber-100 text-amber-700',
                                  log.status === 'no_config' && 'bg-gray-100 text-gray-700'
                                )}>
                                  {log.status === 'sent' ? <CheckCircle size={12} /> : <XCircle size={12} />}
                                  {log.status === 'sent' && 'Enviado'}
                                  {log.status === 'failed' && 'Fallido'}
                                  {log.status === 'no_email' && 'Sin Email'}
                                  {log.status === 'no_config' && 'Sin Config'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    {emailLogs?.pagination && emailLogs.pagination.pages > 1 && (
                      <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
                        <p className="text-sm text-gray-600">
                          Pagina {emailLogs.pagination.page} de {emailLogs.pagination.pages}
                        </p>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setEmailPage(p => Math.max(1, p - 1))}
                            disabled={emailPage === 1}
                            className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                          >
                            <ChevronLeft size={18} />
                          </button>
                          <button
                            onClick={() => setEmailPage(p => Math.min(emailLogs.pagination.pages, p + 1))}
                            disabled={emailPage === emailLogs.pagination.pages}
                            className="p-2 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                          >
                            <ChevronRight size={18} />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Jobs Tab — TODAS las automatizaciones del sistema */}
          {activeTab === 'jobs' && jobs && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <JobCard
                color="blue"
                icon={Server}
                title="Status Checker"
                description="Polling continuo a Hacienda (cada ~30s)"
                status={jobs.statusChecker?.status}
                statusLabel="Activo"
              >
                <Metric label="Documentos pendientes" value={jobs.statusChecker?.pendingDocuments ?? 0} sublabel="en cola hacia Hacienda" />
                <Metric label="Mensajes receptor" value={jobs.statusChecker?.pendingMensajes ?? 0} sublabel="pendientes de envío" />
                {jobs.statusChecker?.lastActivity && (
                  <Metric label="Última actividad" value={fmtRel(jobs.statusChecker.lastActivity)} sublabel="último cambio de estado" />
                )}
              </JobCard>

              <JobCard
                color="purple"
                icon={RefreshCw}
                title="Facturas Recurrentes"
                description="Cron horario (zona CR)"
                status={jobs.recurringInvoices?.status}
                statusLabel={`${jobs.recurringInvoices?.count || 0} activas`}
              >
                {jobs.recurringInvoices?.byClient?.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      {jobs.recurringInvoices.byClient.slice(0, 6).map((c, idx) => (
                        <div key={c.receptorId || idx} className="flex items-center justify-between py-1.5 border-b border-purple-100">
                          <span className="text-sm text-gray-800 truncate" title={c.receptor}>{c.receptor}</span>
                          <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-semibold">{c.cantidad}</span>
                        </div>
                      ))}
                    </div>
                    {jobs.recurringInvoices.byClient.length > 6 && (
                      <details className="mt-2">
                        <summary className="text-sm text-purple-600 cursor-pointer hover:text-purple-800">
                          Ver {jobs.recurringInvoices.byClient.length - 6} clientes más...
                        </summary>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 mt-2 pt-2 border-t border-purple-200">
                          {jobs.recurringInvoices.byClient.slice(6).map((c, idx) => (
                            <div key={c.receptorId || idx} className="flex items-center justify-between py-1.5 border-b border-purple-100">
                              <span className="text-sm text-gray-800 truncate" title={c.receptor}>{c.receptor}</span>
                              <span className="ml-2 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-semibold">{c.cantidad}</span>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </>
                ) : <p className="text-sm text-gray-500 text-center py-4">Sin facturas recurrentes programadas</p>}
              </JobCard>

              <JobCard
                color="amber"
                icon={Bell}
                title="Alertas Facturación API"
                description="Recordatorios para FE pendientes de revisión (hourly)"
                status={jobs.apiDraftsAlerts?.status}
                statusLabel={jobs.apiDraftsAlerts?.status === 'paused' ? 'Pausado' : 'Activo'}
              >
                <Metric label="FE en pending_review" value={jobs.apiDraftsAlerts?.pendingReview ?? 0} sublabel="esperan aprobación humana" />
                <Metric label="Umbral de recordatorio" value={`${jobs.apiDraftsAlerts?.reminderHours || 4}h`} sublabel="luego avisa por correo" />
                <Metric label="Destinatarios" value={jobs.apiDraftsAlerts?.recipients ? jobs.apiDraftsAlerts.recipients.split(/[,;]/).length : 0} sublabel={jobs.apiDraftsAlerts?.recipients || 'sin configurar'} />
              </JobCard>

              <JobCard
                color="cyan"
                icon={Send}
                title="Auto-envío de FE"
                description="Email al cliente al ser aceptada por Hacienda"
                status={jobs.autoEmail?.status}
                statusLabel="Activo"
              >
                <Metric label="Enviados últimas 24h" value={jobs.autoEmail?.sentLast24h ?? 0} sublabel={`${jobs.autoEmail?.totalLast24h ?? 0} intentos totales`} />
                <Metric label="Fallidos" value={jobs.autoEmail?.failedLast24h ?? 0} sublabel={jobs.autoEmail?.failedLast24h > 0 ? '⚠ Revisar logs de email' : 'Sin errores'} alert={jobs.autoEmail?.failedLast24h > 0} />
                {jobs.autoEmail?.lastSent && <Metric label="Último envío" value={fmtRel(jobs.autoEmail.lastSent)} sublabel="exitoso" />}
              </JobCard>

              <JobCard
                color="indigo"
                icon={BookOpen}
                title="Asientos Contables Automáticos"
                description="Genera asiento al aceptar/pagar FE"
                status={jobs.asientosAutomaticos?.status}
                statusLabel="Activo"
              >
                <Metric label="Generados últimas 24h" value={jobs.asientosAutomaticos?.generatedLast24h ?? 0} sublabel="asientos creados" />
                <Metric label="Pendientes (30d)" value={jobs.asientosAutomaticos?.pendientes ?? 0} sublabel={jobs.asientosAutomaticos?.pendientes > 0 ? 'FE aceptadas sin asiento' : 'Todo al día'} alert={jobs.asientosAutomaticos?.pendientes > 0} />
                {jobs.asientosAutomaticos?.lastGenerated && <Metric label="Último asiento" value={fmtRel(jobs.asientosAutomaticos.lastGenerated)} sublabel="autogenerado" />}
              </JobCard>

              <JobCard
                color="rose"
                icon={DownloadCloud}
                title="Importación de Gastos por Email"
                description="Lee bandeja Outlook y crea FE de compra"
                status={jobs.gastosImport?.status}
                statusLabel={jobs.gastosImport?.status === 'disabled' ? 'No configurado' : (jobs.gastosImport?.status === 'paused' ? 'Pausado' : 'Activo')}
              >
                <Metric label="Importados últimas 24h" value={jobs.gastosImport?.importedLast24h ?? 0} sublabel="facturas creadas" />
                <Metric label="Errores" value={jobs.gastosImport?.failedLast24h ?? 0} sublabel={jobs.gastosImport?.failedLast24h > 0 ? '⚠ Revisar log' : 'Sin errores'} alert={jobs.gastosImport?.failedLast24h > 0} />
                {jobs.gastosImport?.lastImport && <Metric label="Última importación" value={fmtRel(jobs.gastosImport.lastImport)} sublabel="ejecutada" />}
              </JobCard>

              <JobCard
                color="slate"
                icon={Webhook}
                title="Renovador de Webhooks"
                description="Microsoft Graph subscriptions auto-renew"
                status={jobs.webhookRenewer?.status}
                statusLabel={jobs.webhookRenewer?.status === 'idle' ? 'Sin webhooks' : 'Activo'}
              >
                <Metric label="Webhooks activos" value={jobs.webhookRenewer?.activeWebhooks ?? 0} sublabel="suscripciones Graph" />
                {jobs.webhookRenewer?.nextRenewal && <Metric label="Próxima renovación" value={fmtRel(jobs.webhookRenewer.nextRenewal)} sublabel="vence el más antiguo" />}
              </JobCard>
            </div>
          )}
        </>
      )}
    </div>
  );
}
